import React, { useEffect, useMemo, useState } from 'react';
import { Checkboxes } from './Checkboxes';
import { VisuallyHidden } from 'components/core';
import {
  CheckboxesBlock,
  CheckboxesBlockValue,
} from 'concepts/Extraction/Blocks/Checkboxes/CheckboxesBlock';
import { isCheckboxesBlockValueEmpty } from 'concepts/Extraction/Blocks/Checkboxes/utils';
import { HydratedBlockProps } from 'concepts/Extraction/Blocks/BaseBlock';
import { useReviewFeatures } from 'hooks/reviews/useReviewFeatures';
import { useAutoDocExtSuggestion } from 'concepts/AutoDocExtraction/useAutoDocExtSuggestion';
import {
  useAutoDocExtSuggestionContext,
  getSingleExtraction,
} from 'concepts/AutoDocExtraction/AutoDocExtSuggestionProvider';
import { deriveExtractionSuggestion } from 'concepts/Extraction/Blocks/TextField/deriveExtractionSuggestion';
import { isFeatureEnabledForField } from 'concepts/AutoDocExtraction/fieldFlags';
import { matchSuggestionToCheckboxOptions } from 'concepts/AutoDocExtraction/optionMatching/matchSuggestionToCheckboxOptions';
import { QuotesSection } from 'concepts/AutoDocExtraction/QuotesSection/QuotesSection';
import { SuggestionActionButtons } from 'concepts/AutoDocExtraction/SuggestionActionButtons/SuggestionActionButtons';
import {
  trackSuggestionViewed,
  trackSuggestionDecision,
  trackSuggestionNotDisplayable,
} from 'concepts/Extraction/Extract/tracking';
import { SuggestionTrigger } from 'concepts/AutoDocExtraction/tracking';
import { usePrevious } from 'hooks/usePrevious';
import { getDE2SuggestionStatusStorageKey } from 'concepts/AutoDocExtraction/suggestionStatusStorage';

export type SuggestibleCheckboxesProps = {
  block: CheckboxesBlock & HydratedBlockProps;
  value: CheckboxesBlockValue;
  comments: string | null;
  onChange: (newValue: CheckboxesBlockValue | null) => void;
  onChangeComments: (newComments: string) => void;
};

const PlainCheckboxes = (props: SuggestibleCheckboxesProps) => (
  <Checkboxes
    editable
    block={props.block}
    value={props.value}
    comments={props.comments}
    onChange={props.onChange}
    onChangeComments={props.onChangeComments}
  />
);

const SuggestibleCheckboxesEnabled = ({
  block,
  value,
  comments,
  onChange,
  onChangeComments,
  autoDocExtField,
}: SuggestibleCheckboxesProps & { autoDocExtField: string }) => {
  const {
    reviewId,
    studyId,
    documentId,
    useSuggestions,
    allSuggestions,
  } = useAutoDocExtSuggestionContext();
  const autoDocumentExtraction = getSingleExtraction(
    allSuggestions,
    autoDocExtField
  );
  const { suggestion } = deriveExtractionSuggestion(autoDocumentExtraction);

  const match = useMemo(
    () =>
      suggestion
        ? matchSuggestionToCheckboxOptions(suggestion, block, autoDocExtField)
        : null,
    [suggestion, block, autoDocExtField]
  );

  const suggestionLabel = useMemo(() => {
    if (!match) return null;
    const matchedLabels = match.matchedOptionIds.map(
      (optionId) =>
        block.options.find((option) => option.id === optionId)?.label
    );
    const otherText = match.suggestedValue.otherOption.selected
      ? [match.suggestedValue.otherOption.value]
      : [];
    return [...matchedLabels, ...otherText].filter(Boolean).join(', ');
  }, [match, block.options]);

  const hasStorageAnchor = documentId !== undefined;
  const storageKey = getDE2SuggestionStatusStorageKey({ reviewId, documentId });

  const {
    showSuggestion,
    accept,
    reject,
    suggestionStatus,
  } = useAutoDocExtSuggestion({
    fieldName: block.id,
    suggestion: suggestionLabel,
    currentValue: isCheckboxesBlockValueEmpty(value) ? null : value,
    enabled: hasStorageAnchor && match !== null,
    storageKey,
  });

  const trackingData = useMemo(
    () => ({
      field: autoDocExtField,
      field_alias: block.label,
      field_type: block.type,
      review_id: reviewId,
      study_id: studyId,
      document_id: documentId ?? null,
      suggestion,
      suggestion_label: suggestionLabel,
    }),
    [
      autoDocExtField,
      block.label,
      block.type,
      reviewId,
      studyId,
      documentId,
      suggestion,
      suggestionLabel,
    ]
  );

  // Fire `metadata_suggestion_viewed` exactly once each time the suggestion
  // UI transitions into being shown.
  const wasPreviouslyShowingSuggestion = usePrevious(showSuggestion);
  useEffect(() => {
    if (!wasPreviouslyShowingSuggestion && showSuggestion && suggestion) {
      trackSuggestionViewed(trackingData);
    }
  }, [wasPreviouslyShowingSuggestion, showSuggestion, suggestion]);

  // Fire `metadata_suggestion_not_displayable` once when a suggestion loads
  // but cannot be shown — a value has no option match and the block has no
  // "other" field to hold it.
  const previousSuggestion = usePrevious(suggestion);
  useEffect(() => {
    if (!previousSuggestion && suggestion && match === null && useSuggestions) {
      trackSuggestionNotDisplayable(trackingData);
    }
  }, [previousSuggestion, suggestion, match, useSuggestions]);

  // Set when the user accepts or rejects so the checkbox group can take focus
  // once the suggestion UI goes away — keyboard users would otherwise lose
  // their place when the button they pressed disappears.
  const [shouldFocusField, setShouldFocusField] = useState(false);

  const handleAccept = (
    trigger: SuggestionTrigger,
    newValue?: CheckboxesBlockValue
  ) => {
    if (!showSuggestion || !match) return;
    setShouldFocusField(true);
    accept();
    onChange(newValue ?? match.suggestedValue);
    trackSuggestionDecision('accepted', { ...trackingData, trigger });
  };

  const handleReject = (trigger: SuggestionTrigger) => {
    if (!showSuggestion) return;
    setShouldFocusField(true);
    reject();
    trackSuggestionDecision('rejected', { ...trackingData, trigger });
  };

  useEffect(() => {
    if (showSuggestion || !shouldFocusField) return;
    const checkboxes = Array.from(
      document.getElementsByName(block.id)
    ) as HTMLInputElement[];
    const target =
      checkboxes.find((checkbox) => checkbox.checked) ?? checkboxes[0];
    target?.focus();
    setShouldFocusField(false);
  }, [showSuggestion, shouldFocusField, block.id]);

  const visibleValue = showSuggestion && match ? match.suggestedValue : value;

  // a checkbox toggle builds on the previous state, so while
  // the suggestion is pending any direct edit accepts it with that
  // modification; the saved value is exactly what is on screen
  const wrappedOnChange = (newValue: CheckboxesBlockValue | null) => {
    if (showSuggestion) {
      handleAccept('input', newValue ?? undefined);
      return;
    }
    onChange(newValue);
  };

  const suggestionPending = showSuggestion && suggestionLabel !== null;
  const suggestionIncludesOther =
    match?.suggestedValue.otherOption.selected ?? false;

  const suggestionButtons = suggestionPending ? (
    <SuggestionActionButtons
      suggestionLabel={suggestionLabel}
      size="md"
      onAccept={() => handleAccept('button')}
      onReject={() => handleReject('button')}
    />
  ) : null;

  const helperId = `${block.id}-suggestion-helper`;
  const otherHelperId = `${block.id}-other-suggestion-helper`;

  return (
    <>
      {suggestionPending && (
        <VisuallyHidden id={helperId}>
          Suggested value, not yet saved. Tab to the Accept and Reject buttons.
        </VisuallyHidden>
      )}
      {suggestionPending && suggestionIncludesOther && (
        <VisuallyHidden id={otherHelperId}>
          Suggested value, not yet saved. Focus this field to accept.
        </VisuallyHidden>
      )}
      <Checkboxes
        editable
        block={block}
        value={visibleValue}
        comments={comments}
        onChange={wrappedOnChange}
        onOtherFieldFocus={() => handleAccept('input')}
        onChangeComments={onChangeComments}
        showSuggestion={showSuggestion}
        checkboxGroupDescribedBy={suggestionPending ? helperId : undefined}
        otherTextFieldDescribedBy={
          suggestionPending && suggestionIncludesOther
            ? otherHelperId
            : undefined
        }
        trailingContent={suggestionButtons}
        hideClearSelection={!!suggestionButtons}
      />
      <QuotesSection
        autoDocExtField={autoDocExtField}
        trackedField={autoDocExtField}
        suggestionStatus={suggestionStatus}
        studyId={studyId}
        extractionTool="extraction2"
      />
    </>
  );
};

const SuggestibleCheckboxes = (props: SuggestibleCheckboxesProps) => {
  const { autoDocExtField } = props.block;
  const { hasFeature } = useReviewFeatures();

  if (
    autoDocExtField !== undefined &&
    isFeatureEnabledForField(autoDocExtField, hasFeature)
  ) {
    return (
      <SuggestibleCheckboxesEnabled
        {...props}
        autoDocExtField={autoDocExtField}
      />
    );
  }

  return <PlainCheckboxes {...props} />;
};

export default SuggestibleCheckboxes;
